import React from 'react';
import { useForm } from 'react-hook-form';
import {
  InputRadio,
  Button,
  Flex,
  FormControl,
  FormControlText,
  Input,
  FormGroup,
  Box
} from '@itandi/itandi-bb-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import type {
  CompanySetting,
  CompanySettingEdit
} from '~/models/CompanySetting';
import { validationSchemaCompanySetting } from '~/models/CompanySetting';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { ConfirmModal } from '~/components/common/parts/modal/ConfirmModal';
import { update } from '~/utils/api/CompanySetting';
import { isSuccess } from '~/utils/api/api_handler';

type Props = {
  companySetting: CompanySetting;
};
export const CompanySettingForm: React.FC<Props> = ({ companySetting }) => {
  const { isOpen, handleOpen, handleClose } = useOpenHandler();
  const { register, getValues, formState, handleSubmit } =
    useForm<CompanySettingEdit>({
      defaultValues: companySetting,
      mode: 'onBlur',
      resolver: yupResolver(validationSchemaCompanySetting)
    });
  const { errors } = formState;
  const onClickSubmit = React.useCallback(() => {
    handleSubmit(() => {
      handleOpen();
    })();
  }, [handleSubmit, handleOpen]);
  const handleAccept = React.useCallback(async () => {
    const values = getValues();
    const response = await update(values);
    if (isSuccess(response)) {
      toast.success('保存しました');
      handleClose();
      return;
    }
    toast.error('保存に失敗しました');
  }, [getValues, handleClose]);
  return (
    <Box width={360}>
      <FormGroup variant="Vertical">
        <FormControl chip="必須" isInvalid={!!errors.name} label="会社名">
          <Input {...register('name')} placeholder="例）サンプル株式会社" />
          <FormControlText error={errors.name?.message} />
        </FormControl>
        <FormControl label="会社代表者氏名">
          <Flex gap={8}>
            <Input {...register('ceoLastName')} placeholder="例）賃貸" />
            <Input {...register('ceoFirstName')} placeholder="例）太郎" />
          </Flex>
        </FormControl>
        <FormControl label="居住用管理戸数">
          <Input {...register('managedUnitNumber')} placeholder="例）500" />
        </FormControl>
        <FormControl
          chip="必須"
          isInvalid={!!errors.defaultInspectionBy}
          label="発注書請書の宛名を補完するか"
        >
          <Flex gap={8}>
            <InputRadio
              {...register('autoFillEstimateAddressType')}
              label="補完しない"
              value="none"
            />
            <InputRadio
              {...register('autoFillEstimateAddressType')}
              label="会社名で補完する"
              value="company_name"
            />
          </Flex>
          <FormControlText
            error={errors.autoFillEstimateAddressType?.message}
          />
        </FormControl>
        <FormControl
          chip="必須"
          isInvalid={!!errors.defaultInspectionBy}
          label="デフォルトの立会設定"
        >
          <Flex gap={8}>
            <InputRadio
              {...register('defaultInspectionBy')}
              label="施工会社"
              value="vendor"
            />
            <InputRadio
              label="管理会社"
              value="company"
              {...register('defaultInspectionBy')}
            />
            <InputRadio
              label="立ち会わない"
              value="none"
              {...register('defaultInspectionBy')}
            />
          </Flex>
          <FormControlText error={errors.defaultInspectionBy?.message} />
        </FormControl>
      </FormGroup>
      <Button
        disabled={!formState.isDirty}
        marginTop={16}
        onClick={onClickSubmit}
        variant="primary"
      >
        保存
      </Button>
      <ConfirmModal
        confirmType="save"
        handleAccept={handleAccept}
        handleCancel={handleClose}
        isOpen={isOpen}
      >
        保存してよろしいですか？
      </ConfirmModal>
    </Box>
  );
};
